import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const ownerWorkSchema = z.object({
  access: z.array(z.record(z.string(), z.unknown())),
  open_items: z.object({ counts: z.record(z.string(), z.number().int().nonnegative()), items: z.array(z.record(z.string(), z.unknown())), generation_as_of: z.string() }).strict(),
  accountant_changes: z.object({ items: z.array(z.record(z.string(), z.unknown())), groups: z.array(z.record(z.string(), z.unknown())), next_cursor: z.string().nullable() }).strict(),
  year_lock: z.object({ locked: z.boolean(), filed_at: z.string().nullable(), by: z.string().nullable().optional(), revision: z.number().int().positive() }).passthrough(),
  retention_notice: z.string(),
}).strict();

export type AccountantOwnerWork = z.infer<typeof ownerWorkSchema>;

const ownerAnswerSchema = z.object({
  open_item: z.object({ open_item_id: z.string(), status: z.enum(['answered']), version: z.number().int().positive(), answered_at: z.string() }).strict(),
}).strict();
const ownerAttestationSchema = z.object({ attestation_id: z.string(), statement_text: z.string(), revision: z.number().int().positive() }).strict();

export function useAccountantOwnerWork(taxYear: number | undefined) {
  return useQuery({
    queryKey: ['business-accountant-work', taxYear ?? null],
    enabled: taxYear != null,
    queryFn: async () => {
      const response = await client.get(`/api/business/year/${taxYear}/accountant-work`);
      return parseEnvelope(ownerWorkSchema, response.data).data;
    },
  });
}

export function useAnswerOwnerOpenItem(taxYear: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { openItemId: string; expectedVersion: number; text: string }) => {
      const response = await client.post(`/api/business/year/${taxYear}/open-items/${input.openItemId}/answer`, {
        expected_version: input.expectedVersion,
        text: input.text,
        document_ids: [],
      });
      return ownerAnswerSchema.parse(response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['business-accountant-work', taxYear] });
      void queryClient.invalidateQueries({ queryKey: ['accountant-open-items'] });
    },
    retry: false,
  });
}

export function useRecordOwnerAttestation(taxYear: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { openItemId: string; kind: string; statementText: string }) => {
      const response = await client.post(`/api/business/year/${taxYear}/open-items/${input.openItemId}/attestation`, {
        kind: input.kind,
        statement_text: input.statementText,
        statement_version: 'owner-open-item-v1',
        facts: { source: 'owner_open_item' },
      });
      return ownerAttestationSchema.parse(response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['business-accountant-work', taxYear] });
    },
    retry: false,
  });
}
